import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [faqVideoData, setEventData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: '',
    content: '',
    url: '',
    category1: 0,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchEventDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('API 요청 시작:', `/api/admin/faqVideo_d/${id}`);

        const response = await axios.get(`/api/admin/faqVideo_d/${id}`);
        console.log('API 응답:', response.data);

        if (response.data && response.data.success !== false) {
          const data = response.data.data;
          setEventData(data);

          // 수정 폼 초기 데이터 설정
          setEditData({
            title: data.fv_title || '',
            content: data.fv_content || '',
            url: data.fv_url || '',
            category1: data.fv_category1 || 0,
          });
          console.log('데이터 설정됨:', data);
        } else {
          console.error('API 응답 오류:', response.data);
          setError(response.data.error || '정보를 불러오는데 실패했습니다.');
        }
      } catch (error) {
        console.error('API 요청 실패:', error);

        let errorMessage = '데이터를 불러오는 중 오류가 발생했습니다.';

        if (error.response) {
          console.error('응답 상태:', error.response.status);
          console.error('응답 데이터:', error.response.data);
          errorMessage = `서버 오류 (${error.response.status}): ${
            error.response.data.message || error.response.statusText
          }`;
        } else if (error.request) {
          console.error('요청 정보:', error.request);
          errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
        } else {
          console.error('오류 메시지:', error.message);
          errorMessage = `요청 오류: ${error.message}`;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEventDetail();
    }
  }, [id]);

  // 파일 선택 처리
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        // 5MB
        alert('파일 크기는 5MB를 초과할 수 없습니다.');
        e.target.value = '';
        return;
      }

      setSelectedFile(file);
    }
  };

  // 수정 모드 전환
  const handleEditClick = () => {
    setIsEditing(true);
  };

  // 수정 취소
  const handleCancelEdit = () => {
    setIsEditing(false);

    // 원래 데이터로 복원
    setEditData({
      title: faqVideoData.fv_title || '',
      content: faqVideoData.fv_content || '',
      url: faqVideoData.fv_url || '',
      category1: faqVideoData.fv_category1 || 0,
    });

    // 파일 선택 초기화
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 입력 필드 변경 처리
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setEditData({
      ...editData,
      [name]:
        type === 'number' || name === 'category1' ? parseInt(value) : value,
    });
  };

  const handleSaveEdit = async () => {
    try {
      if (!editData.title.trim()) {
        alert('제목을 입력해주세요.');
        return;
      }
      if (!editData.content.trim()) {
        alert('내용을 입력해주세요.');
        return;
      }
      console.log('저장할 데이터:', {
        fv_no: id,
        title: editData.title,
        content: editData.content,
        url: editData.url,
        category1: editData.category1,
        hasFile: !!selectedFile,
      });

      let response;

      if (selectedFile) {
        // 파일이 있는 경우: FormData 사용
        const formData = new FormData();
        formData.append('fv_no', id);
        formData.append('title', editData.title);
        formData.append('content', editData.content);
        formData.append('url', editData.url || '');
        formData.append('category1', editData.category1);
        formData.append('file', selectedFile);

        console.log('FormData로 파일 포함 수정 요청');
        response = await axios.post(
          '/api/admin/faqVideo/updateWithFile',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        // 파일이 없는 경우: JSON 사용
        const requestData = {
          fv_no: id,
          title: editData.title,
          content: editData.content,
          url: editData.url || '',
          category1: editData.category1,
        };

        console.log('JSON으로 일반 수정 요청:', requestData);
        response = await axios.post('/api/admin/faqVideo/update', requestData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }

      if (response.data && response.data.success) {
        alert('수정이 완료되었습니다.');

        setIsEditing(false);

        const refreshResponse = await axios.get(`/api/admin/faqVideo_d/${id}`);
        if (refreshResponse.data && refreshResponse.data.success !== false) {
          setEventData(refreshResponse.data.data);
          setEditData({
            title: refreshResponse.data.data.fv_title || '',
            content: refreshResponse.data.data.fv_content || '',
            url: refreshResponse.data.data.fv_url || '',
            category1: refreshResponse.data.data.fv_category1 || 0,
          });
        }

        // 파일 선택 초기화
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        alert(
          '수정에 실패했습니다: ' + (response.data.message || '알 수 없는 오류')
        );
      }
    } catch (error) {
      console.error('수정 요청 실패:', error);
      alert(
        '수정 중 오류가 발생했습니다: ' +
          (error.response?.data?.message || error.message)
      );
    }
  };

  // 파일 확장자 체크 함수
  const isAllowedFileType = (fileName) => {
    if (!fileName) return false;

    const fileExtension = fileName
      .substring(fileName.lastIndexOf('.') + 1)
      .toLowerCase();
    const denyTypes =
      /^(exe|jsp|jspx|jsw|jsv|jspf|phtml|php|php3|php4|php5|inc|js|html|htm)$/;

    return !denyTypes.test(fileExtension);
  };

  // 첨부파일 렌더링
  const renderAttachment = () => {
    if (
      !faqVideoData ||
      !faqVideoData.fv_file1_s ||
      !isAllowedFileType(faqVideoData.fv_file1_s)
    ) {
      return <p>첨부파일 없음</p>;
    }

    // const filePath = `/resources/upload/${faqVideoData.fv_file1_s}`;
    // 이미지 URL을 스프링 서버 경로로 변경 임시)_
    const filePath = `/resources/upload/${faqVideoData.fv_file1_s}`;

    return (
      <div>
        <a
          href={filePath}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: '#007bff' }}
        >
          📎 {faqVideoData.fv_file1_o}
        </a>
      </div>
    );
  };

  // 카테고리 텍스트 반환
  const getCategoryText = (category) => {
    return category === 0 ? '자주묻는질문' : '자가관리방법';
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return format(date, 'yyyy-MM-dd HH:mm');
    } catch (error) {
      console.error('날짜 포맷팅 오류:', error);
      return dateString;
    }
  };

  const handleGoToList = () => {
    navigate('/admin/faq');
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="titleNav">Faq</div>
        <div className="wrap">
          <div
            className="over_hi"
            style={{ marginTop: '60px', textAlign: 'center' }}
          >
            <p>로딩 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-wrapper">
        <div className="titleNav">Faq</div>
        <div className="wrap">
          <div
            className="over_hi"
            style={{ marginTop: '60px', textAlign: 'center' }}
          >
            <p style={{ color: 'red' }}>{error}</p>
            <button
              type="button"
              className="common-del"
              onClick={handleGoToList}
            >
              목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!faqVideoData) {
    return (
      <div className="content-wrapper">
        <div className="titleNav">Faq</div>
        <div className="wrap">
          <div
            className="over_hi"
            style={{ marginTop: '60px', textAlign: 'center' }}
          >
            <p>Faq를 찾을 수 없습니다.</p>
            <button
              type="button"
              className="common-del"
              onClick={handleGoToList}
            >
              목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="content-wrapper">
        <div className="titleNav">Faq</div>
        {/* content S */}
        <div className="">
          <div className="over_hi" style={{ marginTop: '60px' }}>
            <form id="updateFrm" method="post" action="">
              <table className="adminTable">
                <tbody>
                  <tr>
                    {/* <th style={{ width: '200px' }}>카테고리</th> */}
                    {/* <td className="left">
                      {isEditing ? (
                        <div>
                          <label style={{ marginRight: '20px' }}>
                            <input
                              type="radio"
                              name="category1"
                              value={0}
                              checked={editData.category1 == 0}
                              onChange={handleInputChange}
                              style={{ marginRight: '5px' }}
                            />
                            자주묻는질문
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="category1"
                              value={1}
                              checked={editData.category1 == 1}
                              onChange={handleInputChange}
                              style={{ marginRight: '5px' }}
                            />
                            자가관리방법
                          </label>
                        </div>
                      ) : (
                        <p>{getCategoryText(faqVideoData?.fv_category1)}</p>
                      )}
                    </td> */}
                  </tr>
                  <tr>
                    <th style={{ width: '200px' }}>제목</th>
                    <td className="left">
                      {isEditing ? (
                        <input
                          type="text"
                          name="title"
                          value={editData.title}
                          onChange={handleInputChange}
                          style={{ width: '100%', padding: '8px' }}
                          placeholder={
                            editData.category1 == 0
                              ? '자주묻는질문 제목을 입력하세요'
                              : '자가관리방법 제목을 입력하세요'
                          }
                        />
                      ) : (
                        <p>{faqVideoData?.fv_title || '-'}</p>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th style={{ width: '200px' }}>내용</th>
                    <td className="left">
                      {isEditing ? (
                        <textarea
                          name="content"
                          value={editData.content}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            height: '300px',
                            padding: '8px',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                          }}
                          placeholder={
                            editData.category1 == 0
                              ? '자주묻는질문 내용을 입력하세요'
                              : '자가관리방법 내용을 입력하세요'
                          }
                        />
                      ) : (
                        <div
                          style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}
                        >
                          {faqVideoData?.fv_content || '-'}
                        </div>
                      )}
                    </td>
                  </tr>
                  {(isEditing
                    ? editData.category1 == 1
                    : faqVideoData?.fv_category1 == 1) && (
                    <tr>
                      <th style={{ width: '200px' }}>관련 URL</th>
                      <td className="left">
                        {isEditing ? (
                          <input
                            type="url"
                            name="url"
                            value={editData.url}
                            onChange={handleInputChange}
                            style={{ width: '100%', padding: '8px' }}
                            placeholder="https://example.com (선택사항)"
                          />
                        ) : faqVideoData?.fv_url ? (
                          <a
                            href={faqVideoData.fv_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', textDecoration: 'none' }}
                          >
                            {faqVideoData.fv_url}
                          </a>
                        ) : (
                          <p>-</p>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* 첨부파일 필드 - 카테고리가 1(자가관리방법)인 경우에만 표시 */}
                  {(isEditing
                    ? editData.category1 == 1
                    : faqVideoData?.fv_category1 == 1) && (
                    <tr>
                      <th style={{ width: '200px' }}>
                        첨부파일
                        <br />
                        (~5MB)
                      </th>
                      <td className="left" style={{ lineHeight: '1.6' }}>
                        {isEditing ? (
                          <div>
                            <input
                              type="file"
                              onChange={handleFileChange}
                              ref={fileInputRef}
                              style={{ marginBottom: '10px' }}
                              accept="image/*,.pdf,.doc,.docx,.hwp,.txt"
                            />
                            <p style={{ fontSize: '14px', color: '#666' }}>
                              현재 파일: {faqVideoData?.fv_file1_o || '없음'}
                              {selectedFile && (
                                <span
                                  style={{
                                    color: '#4CAF50',
                                    marginLeft: '10px',
                                  }}
                                >
                                  새 파일: {selectedFile.name}
                                </span>
                              )}
                            </p>
                          </div>
                        ) : (
                          renderAttachment()
                        )}
                      </td>
                    </tr>
                  )}

                  <tr>
                    <th style={{ width: '200px' }}>등록일시</th>
                    <td className="left">
                      <p>{formatDate(faqVideoData?.fv_add_dt)}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>

            <div className="right">
              {isEditing ? (
                <>
                  <input
                    type="button"
                    value="저장"
                    className="common-del"
                    onClick={handleSaveEdit}
                    style={{ marginRight: '10px' }}
                  />
                  <input
                    type="button"
                    value="취소"
                    className="common-del"
                    onClick={handleCancelEdit}
                  />
                </>
              ) : (
                <>
                  <input
                    type="button"
                    value="수정하기"
                    className="common-del"
                    onClick={handleEditClick}
                    style={{ marginRight: '10px' }}
                  />

                  <input
                    type="button"
                    value="목록으로"
                    className="common-del"
                    onClick={handleGoToList}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
